import type { ComponentProps } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'

import AccommodationFacilities, {
  getBedbankFacilitiesContent,
  getFlashFacilitiesContent,
  getPropertyFacilitiesContent,
} from 'components/Common/AccommodationDetails/AccommodationFacilities'
import AccommodationThingsToKnow, {
  hasAccommodationThingsToKnow,
  type AccommodationThingsToKnowSource,
} from 'components/Common/AccommodationDetails/AccommodationThingsToKnow'
import LoadingBox from 'components/Common/Loading/LoadingBox'
import { usesPropertyThingsToKnowLayout } from 'components/Common/AccommodationDetails/PropertyThingsToKnowContent'
import Group from 'components/utils/Group'
import { StructuredFacilitiesReworkStandaloneActivation } from 'components/OfferPage/NavigableOfferContent/StructuredFacilitiesReworkActivation'
import { useStructuredFacilitiesReworkForFacilities } from 'components/OfferPage/NavigableOfferContent/useStructuredFacilitiesRework'
import Divider from 'LuxKit/Divider'
import Heading from 'LuxKit/Typography/Heading'

import HotelDetailAmenities from './HotelDetailAmenities'
import type { DrawerHotelAmenity } from './types'
import type { DrawerAccommodationDetailsSource } from './useDrawerAccommodationDetailsSource'

interface Props {
  source: DrawerAccommodationDetailsSource
  dividerKind: ComponentProps<typeof Divider>['kind']
  amenities?: Array<DrawerHotelAmenity>
}

const SectionGroup = styled(Group)`
  position: relative;
`

const ExperimentActivationSlot = styled.div`
  position: absolute;
  inset: 0 0 auto;
`

function getSourceStructuredFacilities(
  source: DrawerAccommodationDetailsSource,
): Array<App.StructuredFacilityGroup> | undefined {
  if (source.kind === 'flash') return source.offer?.structuredFacilities
  if (source.kind === 'property') return source.property?.structuredFacilities
}

function getFacilitiesContent(
  source: DrawerAccommodationDetailsSource,
  structuredFacilities?: Array<App.StructuredFacilityGroup>,
) {
  if (source.kind === 'flash' && source.offer) {
    return getFlashFacilitiesContent(source.offer, structuredFacilities)
  }

  if (source.kind === 'bedbank' && source.offer) {
    return getBedbankFacilitiesContent(source.offer)
  }

  if (source.kind === 'property' && source.property) {
    return getPropertyFacilitiesContent(source.property, structuredFacilities)
  }
}

function getThingsToKnowSource(
  source: DrawerAccommodationDetailsSource,
): AccommodationThingsToKnowSource | undefined {
  if (source.kind === 'flash' && source.offer) {
    return { kind: 'flash', offer: source.offer }
  }

  if (source.kind === 'bedbank' && source.offer) {
    return { kind: 'bedbank', offer: source.offer }
  }

  if (source.kind === 'property' && source.property) {
    return { kind: 'property', property: source.property }
  }
}

function DrawerAccommodationDetailsSections({ source, dividerKind, amenities }: Props) {
  const intl = useIntl()
  const sourceStructuredFacilities = getSourceStructuredFacilities(source)
  const {
    structuredFacilitiesReworkActivationRef,
    structuredFacilitiesRework,
    shouldTrackFacilitiesExperimentInView,
  } = useStructuredFacilitiesReworkForFacilities(sourceStructuredFacilities)
  const facilitiesContent = getFacilitiesContent(source, structuredFacilitiesRework)
  const thingsToKnowSource = getThingsToKnowSource(source)
  const shouldShowThingsToKnow = !!thingsToKnowSource && hasAccommodationThingsToKnow(thingsToKnowSource)
  const shouldShowFallbackAmenities = !source.loading && !facilitiesContent && !!amenities?.length
  const hasDetailsContent = !!facilitiesContent || shouldShowFallbackAmenities || shouldShowThingsToKnow
  const usesThingsToKnowLabel = thingsToKnowSource?.kind !== 'property' ||
    usesPropertyThingsToKnowLayout(thingsToKnowSource.property)
  const activation = shouldTrackFacilitiesExperimentInView ? (
    <ExperimentActivationSlot>
      <StructuredFacilitiesReworkStandaloneActivation
        activationRef={structuredFacilitiesReworkActivationRef}
      />
    </ExperimentActivationSlot>
  ) : null

  if (source.loading && !hasDetailsContent) {
    return (
      <>
        <Divider kind={dividerKind} />
        <Group direction="vertical" gap={12}>
          <LoadingBox height={20} width={120} borderRadius="S" />
          <LoadingBox height={60} width={320} borderRadius="S" />
        </Group>
      </>
    )
  }

  if (!hasDetailsContent && !shouldTrackFacilitiesExperimentInView) {
    return null
  }

  return (
    <>
      {facilitiesContent && (
        <>
          <Divider kind={dividerKind} />
          <SectionGroup direction="vertical" gap={12}>
            {activation}
            <Heading variant="heading4" as="h4">
              {intl.formatMessage({
                id: 'dOZd09w4GyaQT9Hx_3T2',
                defaultMessage: 'Facilities',
                description: 'Section label for the property facilities and amenities on the offer detail page',
              })}
            </Heading>
            <AccommodationFacilities content={facilitiesContent} />
          </SectionGroup>
        </>
      )}

      {shouldShowFallbackAmenities && amenities && (
        <>
          <Divider kind={dividerKind} />
          <HotelDetailAmenities amenities={amenities} />
        </>
      )}

      {shouldShowThingsToKnow && thingsToKnowSource && (
        <>
          <Divider kind={dividerKind} />
          <SectionGroup direction="vertical" gap={12}>
            {!facilitiesContent && activation}
            <Heading variant="heading4" as="h4">
              {usesThingsToKnowLabel ? intl.formatMessage({
                id: '9WsTECHeqsvJ8UhB4MQQ',
                defaultMessage: 'Things to know',
                description: 'Form field label for things to know section on the offer detail page',
              }) : intl.formatMessage({
                id: 'YqdvgkZpA8r_JTJljKDk',
                defaultMessage: 'Fine print',
                description: 'Tab label and heading for fine print section on property page',
              })}
            </Heading>
            <AccommodationThingsToKnow source={thingsToKnowSource} />
          </SectionGroup>
        </>
      )}

      {!facilitiesContent && !shouldShowThingsToKnow && shouldTrackFacilitiesExperimentInView && (
        <StructuredFacilitiesReworkStandaloneActivation
          activationRef={structuredFacilitiesReworkActivationRef}
        />
      )}
    </>
  )
}

export default DrawerAccommodationDetailsSections
